import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import MaskLine from "../MaskLine/MaskLine";
import { DUR, HERO_FLIP_TRANSITION } from "../../lib/motion";
import "./Intro.css";

/*
 * Only the pacing that's specific to this sequence lives here — the reveal
 * itself is MaskLine's own DUR.slow, shared with Home's hero, so the two
 * always move at the same speed and can never quietly drift apart.
 *
 * HOLD_MS is deliberately slower than a typical reveal on this site: this
 * sequence plays once per session, and rushing the one moment that's meant to
 * feel like an entrance defeats the point of having one. The handoff's own
 * duration is HERO_FLIP_TRANSITION (lib/motion.ts), shared with Home's copy
 * of the same elements — kept there rather than here because whichever side
 * of a `layoutId` swap actually governs the animation's timing is decided by
 * Framer, not by us, so both sides must reference one identical object.
 */
const NAME_DELAY = 0.2;
const ROLE_DELAY = 0.75;
/** How long the fully-revealed name sits still before handing off. */
const HOLD_MS = 1200;

type Phase = "reveal" | "hold" | "morph";

interface IntroProps {
    /** Fires the instant Home should start rendering the shared name/role elements. */
    onMorphStart: () => void;
    /** Fires once the handoff has visually finished — safe to unmount Intro entirely. */
    onDone: () => void;
}

/**
 * The site's one-time boot sequence.
 *
 * The name and role here are *the same elements*, in Framer's shared-layout
 * sense, as Home's hero name and role — see lib/introMorph.tsx for how the
 * handoff is coordinated. What plays out on screen is one continuous piece
 * of type sliding from the centre of a blank stage into its resting place in
 * the hero, not two separate reveals stitched together with a fade.
 *
 * Gated in App.tsx to run at most once per browser session and never at all
 * under `prefers-reduced-motion`; this component doesn't need to know either
 * of those rules, it just plays once, however it's mounted.
 */
export default function Intro({ onMorphStart, onDone }: IntroProps) {
    const [phase, setPhase] = useState<Phase>("reveal");

    // reveal -> hold, timed off the name+role reveal durations rather than a
    // guessed number, so this can never quietly drift out of sync with them.
    useEffect(() => {
        const revealEnd = Math.max(NAME_DELAY, ROLE_DELAY) + DUR.slow;
        const t = setTimeout(() => setPhase("hold"), revealEnd * 1000);
        return () => clearTimeout(t);
    }, []);

    // hold -> morph
    useEffect(() => {
        if (phase !== "hold") return;
        const t = setTimeout(() => setPhase("morph"), HOLD_MS);
        return () => clearTimeout(t);
    }, [phase]);

    // The instant we enter "morph", tell Home to mount the shared elements
    // (in the same tick Intro stops rendering its own copies, below) and
    // schedule the full unmount for when the flight animation has had time
    // to land.
    useEffect(() => {
        if (phase !== "morph") return;
        onMorphStart();
        const t = setTimeout(onDone, HERO_FLIP_TRANSITION.duration * 1000);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase]);

    // A click, tap, or keypress jumps straight to the handoff — nobody who
    // has already sat through this once this session, or is just impatient,
    // should be stuck waiting out a fixed timeline to reach their own site.
    useEffect(() => {
        if (phase === "morph") return;
        const skip = () => setPhase("morph");
        window.addEventListener("pointerdown", skip);
        window.addEventListener("keydown", skip);
        return () => {
            window.removeEventListener("pointerdown", skip);
            window.removeEventListener("keydown", skip);
        };
    }, [phase]);

    const owningText = phase !== "morph";

    return (
        <div className="intro" aria-hidden="true">
            <motion.div
                className="intro-bg"
                initial={{ opacity: 1 }}
                animate={{ opacity: owningText ? 1 : 0 }}
                transition={HERO_FLIP_TRANSITION}
            />
            {owningText && (
                <div className="intro-stage">
                    <motion.h1
                        layoutId="shared-hero-name"
                        className="hero-name"
                        transition={HERO_FLIP_TRANSITION}
                    >
                        <MaskLine delay={NAME_DELAY}>Quoc Huy</MaskLine>
                    </motion.h1>
                    <motion.p
                        layoutId="shared-hero-role"
                        className="hero-role-line"
                        transition={HERO_FLIP_TRANSITION}
                    >
                        <MaskLine delay={ROLE_DELAY}>
                            <span className="hero-role mono">Full-Stack Developer</span>
                        </MaskLine>
                    </motion.p>
                </div>
            )}
        </div>
    );
}
